import axios, { AxiosInstance } from 'axios';
import { findDataByLanguage } from '../sew-ws-header.helpers';
import { BasketLink, BasketLinksResponse } from '../types/basket-links.types';
import { BasketContentResponse } from '../types/basket.types';
import { DirectLinks, DirectLinksResponse } from '../types/direct-links.types';
import { LabelsResponse, LabelsRhdoc } from '../types/labels.types';
import { CountrySelector } from '../types/language.types';
import { NavHomeItem, NavigationResponse } from '../types/navigation.types';
import { DomainInfo } from '../types/os-domain.types';
import { Params } from '../types/params.types';
import { Permission, PermissionStatus } from '../types/permission.types';
import {
    ApiPortalConfig,
    ProjectMetaDataResponse,
    ProjectMetaDataRhdoc,
} from '../types/project-metadata.types';
import { User } from '../types/user.types';

export class HttpClient {
    private baseUrl: string;
    private caasBaseUrl: string;
    /**
     * CSRF Token is needed for data manipulating requests (PUT, POST, DELETE) against WZ
     */
    private csrfToken?: string;

    /**
     * If the preview is enabled, there should not be any requests vs the WZ backend
     */
    private isPreview: boolean;

    private httpClient: AxiosInstance;
    private apiPortalConfig: ApiPortalConfig = { enabled: false };

    constructor(baseUrl: string, caasBaseUrl: string, isPreview: boolean) {
        this.baseUrl = baseUrl;
        this.caasBaseUrl = caasBaseUrl;
        this.isPreview = isPreview;

        this.httpClient = axios.create();
    }

    public setApiPortalConfig(apiPortalConfig: ApiPortalConfig): void {
        this.apiPortalConfig = apiPortalConfig;
    }

    public async changeLanguageOfBackendUser(newLngCode: string): Promise<void> {
        if (this.isPreview) {
            return Promise.resolve();
        }

        return this.httpClient.get(
            `${
                this.baseUrl
            }/os/c/main.do?page=BLANK&addon=USER&action=USER-SAVE_USER_LANG&language=${newLngCode.toLowerCase()}`,
            {
                withCredentials: true,
            }
        );
    }

    public async fetchBasketContent(): Promise<BasketContentResponse> {
        if (this.isPreview) {
            // TODO: Replace after Endpoint was adjusted
            return Promise.resolve({ article: '', quantity: '', unit: '', content: [] });
        }

        return this.httpClient
            .get<BasketContentResponse>(`${this.baseUrl}/os/c/main.do?addon=BASKET&page=NAVQUICK_CONTENT`, {
                headers: { Accept: 'application/json' },
                withCredentials: true,
            })
            .then(response => response.data);
    }

    public async fetchBasketLinks(language: string, countryCode: string): Promise<BasketLink[]> {
        language = language.toUpperCase();
        const url = `${this.caasBaseUrl}/direct/SEW${countryCode}/basket-links?filter={'fs_language':'${language}'}`;

        return this.httpClient
            .get<BasketLinksResponse>(url)
            .then(response => findDataByLanguage(response, language))
            .then(response => response.basket_links)
            .catch(() => []);
    }

    public async fetchDirectLinks(language: string, countryCode: string): Promise<DirectLinks> {
        language = language.toUpperCase();
        const url = `${this.caasBaseUrl}/direct/SEW${countryCode}/direct-links?filter={'fs_language':'${language}'}`;

        return this.httpClient
            .get<DirectLinksResponse>(url)
            .then(response => findDataByLanguage(response, language))
            .catch(() => ({ headline: '', links: [], fs_language: '' }));
    }

    public async fetchDomainInfo(): Promise<DomainInfo | null> {
        if (this.isPreview) {
            return Promise.resolve(null);
        }

        return this.httpClient
            .get<DomainInfo>(`${this.baseUrl}/os/api/service/domain`, { withCredentials: true })
            .then(response => response.data)
            .catch(() => null);
    }

    public async fetchLabels(language: string, countryCode: string): Promise<LabelsRhdoc> {
        language = language.toUpperCase();
        const url = `${this.caasBaseUrl}/direct/SEW${countryCode}/dictionary?filter={'fs_language':'${language}'}`;

        return this.httpClient
            .get<LabelsResponse>(url)
            .then(response => findDataByLanguage(response, language));
    }

    public async fetchLanguages(language: string, countryCode: string): Promise<CountrySelector> {
        countryCode = countryCode.toUpperCase();
        // for requests to the WZ we have to use GB, for requests to caas we have to use UK
        countryCode = countryCode.replace('UK', 'GB');
        const url = `${this.baseUrl}/os/api/service/country-selector/${countryCode}/${language}`;

        return this.httpClient.get<CountrySelector>(url).then(response => response.data);
    }

    public async fetchNavigation(language: string, countryCode: string): Promise<NavHomeItem> {
        language = language.toUpperCase();
        const url = `${this.caasBaseUrl}/direct/SEW${countryCode}/main-navigation?filter={'fs_language':'${language}'}`;

        return this.httpClient
            .get<NavigationResponse>(url)
            .then(response => findDataByLanguage(response, language));
    }

    public async fetchPermissions(): Promise<Permission[]> {
        if (this.isPreview) {
            return Promise.resolve([]);
        }

        return this.httpClient
            .get<Permission[]>(`${this.baseUrl}/os/api/user/perm`, {
                withCredentials: true,
            })
            .then(response => response.data)
            .catch(() => []);
    }

    public async fetchPermissionDependedData(
        language: string,
        countryCode: string
    ): Promise<[NavHomeItem, Permission[]]> {
        language = language.toUpperCase();

        return axios
            .all<
                NavHomeItem | BasketLink[] | Permission[]
            >([this.fetchNavigation(language, countryCode), this.fetchPermissions()])
            .then(responses => {
                const navHomeItem = responses[0] as NavHomeItem;
                const permissions = responses[1] as Permission[];

                return [navHomeItem, permissions];
            });
    }

    public async fetchPermissionStatus(permission: string): Promise<Permission> {
        if (this.isPreview) {
            return Promise.resolve({
                auth: permission,
                status: PermissionStatus.NO_PERMISSION,
            } as Permission);
        }

        return this.httpClient
            .get<Permission>(`${this.baseUrl}/os/api/user/perm/${permission}`)
            .then(response => response.data)
            .catch(() => ({ auth: permission, status: PermissionStatus.NO_PERMISSION }) as Permission);
    }

    public async fetchProjectMetaData(language: string, countryCode: string): Promise<ProjectMetaDataRhdoc> {
        language = language.toUpperCase();
        const url = `${this.caasBaseUrl}/direct/SEW${countryCode}/project_metadata?filter={'fs_language':'${language}'}`;

        return this.httpClient
            .get<ProjectMetaDataResponse>(url)
            .then(response => response.data._embedded['rh:doc'][0]);
    }

    //TODO: remove this call, when OIDC works well in Production!
    public async fetchUseOidc(countryCode: string): Promise<boolean> {
        const url = `${this.caasBaseUrl}/direct/SEW${countryCode}/project_metadata`;

        return this.httpClient
            .get<ProjectMetaDataResponse>(url)
            .then(response => response.data._embedded['rh:doc'][0].useOIDC);
    }

    public async fetchSearchHistory(): Promise<string[]> {
        if (this.isPreview) {
            return Promise.resolve([]);
        }

        const useApiPortal = this.isApiPortalEnabledAndConfigured();

        const url = useApiPortal
            ? `${this.baseUrl}/gateway/os-user-core/1.0.0/component/header/search-history`
            : `${this.baseUrl}/os/api/user/param/header/search-history`;

        return this.httpClient
            .get<string[]>(url, {
                withCredentials: true,
                headers: {
                    ...(useApiPortal && {
                        'x-Gateway-APIKey': this.apiPortalConfig.apiKey,
                    }),
                },
            })
            .then(response => response.data)
            .catch(() => []);
    }

    public async fetchSearchRecommendations(
        language: string,
        countryCode: string,
        searchTerm: string
    ): Promise<string[]> {
        if (this.isPreview) {
            return Promise.resolve([]);
        }

        language = language.toLowerCase();

        return this.httpClient
            .get<string[]>(`${this.baseUrl}/api/v1/quicksearch/${countryCode.toUpperCase()}/${language}`, {
                params: { q: searchTerm },
                withCredentials: true,
            })
            .then(response => response.data)
            .catch(() => []);
    }

    public async fetchSvgLogo(url: string): Promise<string | null> {
        return this.httpClient
            .get<string>(url + '?SEWNoSSO=true')
            .then(response => response.data)
            .catch(() => null);
    }

    public fetchToken(): Promise<void> {
        if (this.isPreview) {
            return Promise.resolve();
        }

        const csrfHeader = 'x-csrf-token';

        return this.httpClient
            .get<number>(`${this.baseUrl}/os/api/os-core/v1/token`, {
                withCredentials: true,
                headers: {
                    [csrfHeader]: 'fetch',
                },
            })
            .then(response => {
                this.csrfToken = response.headers[csrfHeader];
                const validity = response.data;
                // refresh CSRF Token after validity is over
                setTimeout(() => this.fetchToken(), validity * 900);
            });
    }

    public async fetchUserData(accessToken?: string): Promise<User | null> {
        if (this.isPreview) {
            return Promise.resolve(null);
        }

        if (accessToken) {
            return this.httpClient
                .get<User>(`${this.baseUrl}/os/api/user/data`, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                })
                .then(response => response.data)
                .catch(() => null);
        } else {
            return this.httpClient
                .get<User>(`${this.baseUrl}/os/api/user/data`, {
                    withCredentials: true,
                })
                .then(response => response.data)
                .catch(() => null);
        }
    }

    public async fetchUserLogin(): Promise<{ isloggedin: boolean }> {
        if (this.isPreview) {
            return Promise.resolve({ isloggedin: false });
        }

        return this.httpClient
            .get(`${this.baseUrl}/os/api/user/isloggedin`, { withCredentials: true })
            .then(response => response.data)
            .catch(() => {
                return { isloggedin: false };
            });
    }

    public requestPermission(permission: string): void {
        if (this.isPreview) {
            return;
        }

        if (this.csrfToken === undefined) {
            this.fetchToken().then(() => this.requestPermission(permission));

            return;
        }

        const formData = new FormData();
        formData.append('action', 'USER-AUTH_REQUEST');
        formData.append('page', 'APPLICATION_REQUEST');
        formData.append('addon', 'USER');
        formData.append('AUTH', permission);

        this.httpClient.post(`${this.baseUrl}/os/c/main.do`, formData, {
            responseType: 'text',
            headers: { 'x-csrf-token': this.csrfToken },
            withCredentials: true,
        });
    }

    public storeSearchHistory(searchTerm: string): Promise<void> {
        if (this.isPreview) {
            return Promise.resolve();
        }

        if (this.csrfToken === undefined) {
            return this.fetchToken().then(() => this.storeSearchHistory(searchTerm));
        }

        const useApiPortal = this.isApiPortalEnabledAndConfigured();

        const url = useApiPortal
            ? `${this.baseUrl}/gateway/os-user-core/1.0.0/component/header/search-history`
            : `${this.baseUrl}/os/api/user/param/header/search-history`;

        return this.httpClient.post(url, searchTerm, {
            headers: {
                'Content-Type': 'text/plain',
                'x-csrf-token': this.csrfToken,
                ...(useApiPortal && {
                    'x-Gateway-APIKey': this.apiPortalConfig.apiKey,
                }),
            },
            withCredentials: true,
        });
    }

    public fetchIDPUsage() {
        if (this.isPreview) {
            return Promise.resolve(false);
        }

        return this.httpClient
            .get(`${this.baseUrl}/os/api/service/params/USER/B2C_LOGIN_ACTIVE`, { withCredentials: true })
            .then(response => response.data)
            .then((response: Params) => {
                const idpParam = response.find(param => param.param === 'B2C_LOGIN_ACTIVE');
                if (idpParam) {
                    return idpParam.value === 'X';
                }

                return false;
            })
            .catch(() => {
                return false;
            });
    }

    public fetchRequestByUrl(url: string) {
        return this.httpClient.get(url).catch(() => Promise.resolve(null));
    }

    private isApiPortalEnabledAndConfigured() {
        return this.apiPortalConfig.enabled && this.apiPortalConfig.apiKey?.length;
    }
}
